import React, { useState, useEffect, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../../contexts/AuthContext';

// Determine API URL: prioritize stored, then local dev, then env, else empty
const host = window.location.hostname;
const isLocal = host === 'localhost' || host === '127.0.0.1';
const storedApiUrl = localStorage.getItem('api_url');
let API_URL;
if (storedApiUrl) {
  API_URL = storedApiUrl;
} else if (isLocal) {
  API_URL = 'https://nivaas.app';
} else if (process.env.REACT_APP_API_URL) {
  API_URL = process.env.REACT_APP_API_URL;
} else {
  API_URL = '';
}

const OrganizationStatusBanner = () => {
  const { currentUser } = useContext(AuthContext);
  const [showBanner, setShowBanner] = useState(false);
  const [bannerMessage, setBannerMessage] = useState('');
  const [bannerType, setBannerType] = useState('info'); // info, warning, danger
  const location = useLocation();

  useEffect(() => {
    // Skip for login/public pages or if user not logged in or if user is maintenance role
    if (!currentUser || location.pathname.includes('/login') || currentUser.role === 'maintenance') {
      setShowBanner(false);
      return;
    }

    // Skip for superadmins
    if (currentUser.role === 'superadmin') {
      setShowBanner(false);
      return;
    }

    const checkOrganizationStatus = async () => {
      try {
        const orgId = currentUser.organization_id;
        if (!orgId) {
          console.log('No organization ID found, cannot check status');
          return;
        }

        const response = await axios.get(`${API_URL}/api/saas/tenants/${orgId}/status`);

        // Determine if we need to show a banner
        if (response.data.status !== 'active' && response.data.subscription_status !== 'active') {
          // Organization is not active
          setBannerMessage('Your organization account is not active. Please contact support.');
          setBannerType('danger');
          setShowBanner(true);
          return;
        }

        // Check payment status
        if (response.data.payment_status === 'unpaid' && response.data.subscription_end_date) {
          const endDate = new Date(response.data.subscription_end_date);
          const daysUntilExpiry = Math.ceil((endDate - new Date()) / (1000 * 60 * 60 * 24));

          if (daysUntilExpiry <= 0) {
            // Subscription has expired
            setBannerMessage('Your subscription has expired. Please update your payment method to continue service.');
            setBannerType('danger');
            setShowBanner(true);
          } else if (daysUntilExpiry <= 7) {
            // Subscription expiring soon
            setBannerMessage(`Your subscription expires in ${daysUntilExpiry} day(s). Please update your payment method.`);
            setBannerType('warning');
            setShowBanner(true);
          }
        }
      } catch (error) {
        console.log('Error checking organization status:', error);
        // Don't show banner on error - silent fail
      }
    };

    checkOrganizationStatus();

    // Check status every hour
    const interval = setInterval(checkOrganizationStatus, 60 * 60 * 1000);

    return () => clearInterval(interval);
  }, [currentUser, location.pathname]);

  // Don't render if no banner to show
  if (!showBanner) return null;

  // Render the banner
  return (
    <div className={`alert alert-${bannerType} mb-0 text-center py-2 d-flex justify-content-center align-items-center`} role="alert">
      <span>{bannerMessage}</span>
      {currentUser?.role === 'admin' && bannerType === 'danger' && (
        <div className="ms-3">
          <a href="/admin/membership" className="btn btn-sm btn-light me-2">Manage Subscription</a>
          <a href="/support" className="btn btn-sm btn-outline-light">Contact Support</a>
        </div>
      )}
    </div>
  );
};

export default OrganizationStatusBanner; 